import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { get, isEmpty, noop, map as mapSpecificKey } from 'lodash'
import {
  Control,
  Controller,
  FieldErrorsImpl,
  FieldValues,
  GlobalError,
  Path,
} from 'react-hook-form'
import {
  SvgClearCross,
  SvgCrossForChip,
  SvgFilledCross,
  SvgPdfIcon,
} from '../../assets/images'
import SearchBar from '../SearchBar'
import {
  DocumentDropdownType,
  FIELD_VALUE_CONSTANTS,
  NAMESPACE,
} from '../../utils'
import ReactDOM from 'react-dom'
import Tooltip from '../Tooltip'
import SvgDropdownArrowGrey from '../../assets/images/components/SvgDropdownArrowGrey'
import { ShortcutKeys } from '../../utils/tab-indexing-contants'
import { FocusTrap } from 'focus-trap-react'

interface OutsideClickHandlerProps {
  refs: React.RefObject<HTMLElement>[]
  handler: () => void
}

const useOutsideClick = ({ refs, handler }: OutsideClickHandlerProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !refs?.some((ref: React.RefObject<HTMLElement>) =>
          ref?.current?.contains(event.target as Node)
        )
      ) {
        handler()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [refs, handler])
}

const getMultiSelectSelectedValues = (
  defaultValue?: string | string[] | null
): Set<string> => {
  if (defaultValue == null || defaultValue === '') {
    return new Set()
  }

  const values = Array.isArray(defaultValue)
    ? defaultValue
    : String(defaultValue).split(',')

  return new Set(values.map((value) => String(value).trim()).filter(Boolean))
}

const isMultiSelectValueSelected = (
  defaultValue: string | string[] | undefined,
  optionValue: unknown
): boolean =>
  getMultiSelectSelectedValues(defaultValue).has(String(optionValue))

const SELECT_ALL_OPTION_VALUE = '__MULTI_SELECT_ALL__'
const SELECT_ALL_OPTION_LABEL = 'Select All'

interface DropdownType<T> {
  value: string
  label: string
  disabled?: boolean
  [key: string]: T | string | boolean | undefined | ReactNode
}

interface OptionContainerPropsType<T> {
  option: DropdownType<T>
  handleValueSelect: (e: DropdownType<T>) => void
  isSelected: boolean
  disabled?: boolean
  isFocused: boolean
  customStyles?: object
}

const OptionContainer = <T,>({
  option,
  handleValueSelect,
  isSelected,
  disabled,
  isFocused,
  customStyles = {},
}: OptionContainerPropsType<T>) => {
  const itemRef = useRef<HTMLLIElement | null>(null)
  const handleSelected = () => {
    if (!isEmpty(option) && !disabled) {
      handleValueSelect(option)
    }
  }

  useEffect(() => {
    if (isFocused && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isFocused])
  return (
    <li
      className={`p-3 border-t border-border-100 hover:bg-secondary-25 flex items-center  pointer-events-auto cursor-pointer w-full text-black font-primary ${
        isFocused ? 'bg-secondary-100' : ''
      }`}
      style={{ ...customStyles }}
      onClick={handleSelected}
      title={option?.tooltip?.toString() ?? option?.label}
      ref={itemRef}
    >
      <input
        type="checkbox"
        className="w-[1.125rem] h-[1.125rem] accent-secondary-500"
        checked={isSelected}
        onChange={noop}
        disabled={disabled}
        tabIndex={-1}
      />

      <span
        style={{ ...customStyles }}
        className="max-w-[calc(100%-0.938rem)] truncate pl-3 font-light text-xs"
      >
        {option?.label}
      </span>
    </li>
  )
}

interface OptionBoxPropsType<T> {
  dropdownOptions: DropdownType<T>[]
  excludedParentRef: React.RefObject<HTMLElement>
  handleValueSelect: (e: DropdownType<T>) => void
  handleOptionFalse: () => void
  toggleOption: () => void
  isOpenOptions: boolean
  selectedOption: DropdownType<T>[]
  isSearchable?: boolean
  fetchOptions?: (
    page: number,
    limit: number,
    value?: string
  ) => Promise<Response>
  limit?: number
  virtualScroll?: boolean
  setPage?: React.Dispatch<React.SetStateAction<number>>
  page?: number
  disabledDefaultValues?: string[]
  position: { top: number; left: number; width: number; maxHeight?: number }
  optionZIndex: number
  popupClassname?: string
  customCssClass?: string
  showFilterByText?: boolean
  handleOptionSearch?: (value?: string) => Promise<void> | undefined | void
  searchLength?: number
  customStyles?: object
  hasSelectAllOption?: boolean
  onSelectAll?: (visibleOptions: DropdownType<T>[]) => void
}
interface FieldErrors<U extends FieldValues> {
  root?: Record<string, GlobalError> & GlobalError
  [key: string]: FieldErrorsImpl<U> | GlobalError | undefined
}

interface MultiSelectPropTypes<T, U extends FieldValues> {
  label: string
  options: DropdownType<T>[]
  name: string
  defaultValue?: string[]
  disabled?: boolean
  disabledDefaultValues?: string[]
  required?: boolean
  onChange: (value: DropdownType<T>[], name: string) => void
  placeholder?: string
  className?: string
  errors?: FieldErrors<U>
  noOfChips?: number
  isSearchable?: boolean
  confirmationModal?: (id: number | null) => Promise<boolean | undefined>
  dataQaId?: string
  showTooltip?: boolean
  showFilterByText?: boolean
  optionZIndex?: number
  fetchOptions?: (
    page: number,
    limit: number,
    vaue?: string
  ) => Promise<Response>
  limit?: number
  virtualScroll?: boolean
  hasPermission?: boolean
  popupClassname?: string
  customCssClass?: string
  handleOptionSearch?: (
    page: number,
    limit: number,
    vaue?: string
  ) => Promise<void>
  sortSelectedItems?: (selectedOptions: DropdownType<T>[]) => DropdownType<T>[]
  searchLength?: number
  modifiedTooltip?: string[]
  chipDataWidth?: string
  tabIndex?: number
  onChipClick?: (value: DocumentDropdownType<T>) => void
  customStyles?: object
  constrainHeight?: boolean
  hasSelectAllOption?: boolean
}

const OptionBox = <T,>({
  dropdownOptions,
  excludedParentRef,
  handleValueSelect,
  handleOptionFalse,
  isOpenOptions,
  selectedOption,
  isSearchable,
  virtualScroll,
  fetchOptions,
  limit,
  setPage,
  disabledDefaultValues,
  position,
  optionZIndex,
  popupClassname = '',
  showFilterByText = true,
  handleOptionSearch,
  searchLength = 3,
  toggleOption,
  customCssClass = '',
  customStyles = {},
  hasSelectAllOption = false,
  onSelectAll,
}: OptionBoxPropsType<T>) => {
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
  const optionsRef = useRef<HTMLUListElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)

  useOutsideClick({
    refs: [excludedParentRef, dropdownMenuRef],
    handler: handleOptionFalse,
  })
  const [options, setOptions] = useState<DropdownType<T>[]>(dropdownOptions)
  const fetchItems = async (page: number, limit: number) => {
    if (loading || !hasMore || !fetchOptions) return // Prevent multiple API calls at once
    setLoading(true)
    try {
      const response = await fetchOptions(page, limit) // Fetch new data
      const data: DropdownType<T>[] = response.json
        ? await response.json()
        : response
      if (limit && data.length < limit) {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }
  const handleSearch = async (e: string) => {
    if ((e?.length === 0 || e?.length >= searchLength) && handleOptionSearch) {
      setLoading(true)
      const data = await handleOptionSearch(e)
      if (limit && Array.isArray(data) && data.length < limit) {
        setHasMore(false)
      } else if (limit && Array.isArray(data) && data.length === limit) {
        setHasMore(true)
      }
      setPage && setPage(0)
      setLoading(false)
    } else {
      if (e === '') {
        setOptions(dropdownOptions)
        return
      }
      const searchValue = e.toLowerCase()
      const filteredOptions = dropdownOptions.filter((option) =>
        option?.label?.toLowerCase().includes(searchValue)
      )
      setOptions(filteredOptions)
    }
  }
  const handleScroll = () => {
    const optionsElement = optionsRef.current
    if (!optionsElement) return

    const scrollTop = optionsElement.scrollTop
    const scrollHeight = optionsElement.scrollHeight
    const offsetHeight = optionsElement.offsetHeight

    if (scrollTop + offsetHeight >= scrollHeight - 10 && hasMore && !loading) {
      setPage &&
        setPage((prevPage) => {
          const newPage = prevPage + 1
          fetchItems(newPage, limit ?? 0)
          return newPage
        })
    }
  }

  useEffect(() => {
    if (optionsRef.current && virtualScroll) {
      optionsRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (optionsRef.current && virtualScroll) {
        optionsRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [loading, hasMore, virtualScroll])

  useEffect(() => {
    setOptions(dropdownOptions)
  }, [dropdownOptions])

  const disabledDefaultValue = (value: string) => {
    return disabledDefaultValues?.some((val) => val === value)
  }

  const areAllVisibleOptionsSelected =
    options.length > 0 &&
    options.every((option) =>
      mapSpecificKey(selectedOption, 'value').includes(option?.value)
    )

  const handleSelectAllClick = () => {
    onSelectAll?.(options)
  }

  const getNavigableItemCount = () =>
    options.length + (hasSelectAllOption && options.length > 0 ? 1 : 0)

  const handleNavigableItemSelect = (index: number) => {
    if (hasSelectAllOption && options.length > 0 && index === 0) {
      handleSelectAllClick()
      return
    }

    const optionIndex =
      hasSelectAllOption && options.length > 0 ? index - 1 : index
    if (optionIndex >= 0 && options[optionIndex]) {
      handleValueSelect(options[optionIndex])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const navigableItemCount = getNavigableItemCount()

    switch (e.key) {
      case ShortcutKeys.Enter:
      case ShortcutKeys.Space:
        e.preventDefault()
        if (!isOpenOptions) {
          toggleOption()
        } else if (focusedIndex >= 0) {
          handleNavigableItemSelect(focusedIndex)
        }
        break

      case ShortcutKeys.ArrowDown:
        e.preventDefault()
        if (isOpenOptions && navigableItemCount > 0) {
          setFocusedIndex((prev) => (prev + 1) % navigableItemCount)
        }
        break

      case ShortcutKeys.ArrowUp:
        e.preventDefault()
        if (isOpenOptions && navigableItemCount > 0) {
          setFocusedIndex(
            (prev) => (prev - 1 + navigableItemCount) % navigableItemCount
          )
        }
        break

      case ShortcutKeys.Shift:
      case ShortcutKeys.Tab:
        if (!isSearchable) {
          toggleOption()
          setFocusedIndex(-1)
        }
        break
      default:
        if (isOpenOptions) {
          e.preventDefault()
          toggleOption()
          setFocusedIndex(-1)
        }
        break
    }
  }
  const handleSearchBarKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e?.key === ShortcutKeys.Escape) {
      toggleOption()
      setFocusedIndex(-1)
    } else if (e?.key === ShortcutKeys.Tab || e?.key === ShortcutKeys.Shift) {
      if (!options?.length) {
        toggleOption()
        setFocusedIndex(-1)
      }
    }
  }
  useEffect(() => {
    if (isOpenOptions) {
      setFocusedIndex(0)
      previouslyFocusedElementRef.current =
        document.activeElement as HTMLElement
      optionsRef?.current?.focus()
    } else {
      previouslyFocusedElementRef.current?.focus()
      setFocusedIndex(-1)
    }
    return () => {
      previouslyFocusedElementRef.current?.focus()
    }
  }, [isOpenOptions])
  return (
    <FocusTrap
      active={isOpenOptions}
      focusTrapOptions={{ allowOutsideClick: true, escapeDeactivates: true }}
    >
      <div
        ref={dropdownMenuRef}
        className={`flex flex-col font-medium ${popupClassname}  mt-[0.006rem] mr-5 rounded-b  z-[9999] border-r border-l border-b  bg-accent-0 ${
          !isSearchable ? 'max-h-[16.375rem]' : 'max-h-[16.90rem]'
        } group-hover:border-t-0 
       ${isOpenOptions ? 'border-secondary-500' : ' border-accent-600'}
      group-hover:border-secondary-500 group-focus-within:border-secondary-500`}
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          width: position.width + 'px',
          zIndex: optionZIndex,
          ...(position.maxHeight !== undefined && {
            maxHeight: position.maxHeight + 'px',
          }),
        }}
      >
        {!isSearchable && !options?.length && (
          <button
            style={{
              // This button is just for fallback condition
              opacity: 0,
              position: 'absolute',
              pointerEvents: 'none',
            }}
            tabIndex={0}
            aria-hidden="true"
          >
            Fallback
          </button>
        )}
        {isSearchable && (
          <div className="p-2 border-t border-accent-100">
            {showFilterByText && (
              <span className="text-[12px] font-medium text-accent-700">
                {NAMESPACE.FILTER_BY}
              </span>
            )}
            <SearchBar
              searchLength={searchLength ?? 3}
              onSearch={handleSearch}
              showFunnel={false}
              width="auto"
              onKeyDown={handleSearchBarKeyDown}
            />
          </div>
        )}
        <ul
          className={`flex-1 options overflow-y-auto min-h-0 ${customCssClass}`}
          ref={optionsRef}
          onKeyDown={handleKeyDown}
          tabIndex={options?.length ? 0 : -1}
        >
          {hasSelectAllOption && options.length > 0 && (
            <OptionContainer
              customStyles={customStyles}
              key={SELECT_ALL_OPTION_VALUE}
              option={{
                label: SELECT_ALL_OPTION_LABEL,
                value: SELECT_ALL_OPTION_VALUE,
              }}
              handleValueSelect={() => handleSelectAllClick()}
              isSelected={areAllVisibleOptionsSelected}
              isFocused={focusedIndex === 0}
            />
          )}
          {options.map((option: DropdownType<T>, index: number) => (
            <OptionContainer
              customStyles={customStyles}
              key={`${option?.value}_${index}`}
              option={option}
              handleValueSelect={handleValueSelect}
              isSelected={mapSpecificKey(selectedOption, 'value').includes(
                option?.value
              )}
              disabled={disabledDefaultValue(option?.value)}
              isFocused={
                hasSelectAllOption && options.length > 0
                  ? index + 1 === focusedIndex
                  : index === focusedIndex
              }
            />
          ))}
          {options?.length === 0 && (
            <li className="p-3 border-t border-border-100 hover:bg-secondary-25 flex justify-center items-center  pointer-events-none w-full text-black font-primary">
              <span className="truncate font-light text-center">
                No Data found
              </span>
            </li>
          )}
          {loading && (
            <div className="text-center p-2 text-accent-500 font-light text-xs">
              Loading...
            </div>
          )}
        </ul>
      </div>
    </FocusTrap>
  )
}

export const MultiSelect = <T, U extends FieldValues>({
  label = '',
  name = '',
  options,
  defaultValue,
  disabled = false,
  required = false,
  onChange = noop,
  placeholder = 'Select Option',
  className = '',
  errors,
  // noOfChips = 1,
  isSearchable,
  optionZIndex = 50,
  confirmationModal,
  dataQaId = '',
  fetchOptions,
  limit,
  virtualScroll,
  showTooltip = true,
  disabledDefaultValues,
  hasPermission = true,
  popupClassname = '',
  showFilterByText = true,
  handleOptionSearch,
  sortSelectedItems,
  searchLength = 3,
  modifiedTooltip,
  tabIndex,
  chipDataWidth = 'max-w-[6.25rem]',
  customCssClass = '',
  onChipClick,
  customStyles = {},
  constrainHeight = false,
  hasSelectAllOption = false,
}: MultiSelectPropTypes<T, U>) => {
  const [isOpenOptions, setIsOpenOptions] = useState(false)
  const textFieldRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{
    top: number
    left: number
    width: number
    maxHeight?: number
  }>({ top: 0, left: 0, width: 200 })
  const [selectedOption, setSelectedOption] = useState<DropdownType<T>[]>([])
  const dropdownRef = useRef<HTMLDivElement>(null)
  // const ListArr = new Array(noOfChips).fill('1')
  const [currentPage, setCurrentPage] = useState(0)
  const [modifiedTooltipArray, setModifiedTooltipArray] = useState<string[]>([])
  const [visibleChips, setVisibleChips] = useState<number>(0)
  const [, setChipWidths] = useState<number[]>([])
  const chipContainerRef = useRef<HTMLDivElement>(null)

  const calculateVisibleChips = useCallback(() => {
    if (
      !textFieldRef.current ||
      !selectedOption.length ||
      !chipContainerRef.current
    ) {
      setVisibleChips(0)
      setChipWidths([])
      return
    }
    const dropdownRect = textFieldRef.current.getBoundingClientRect()
    const availableWidth = dropdownRect.width - 50
    const widths: number[] = []
    selectedOption.forEach((_, index) => {
      const chipElement = chipContainerRef.current?.children[
        index
      ] as HTMLElement
      if (chipElement) {
        const chipWidth = chipElement.getBoundingClientRect().width
        widths.push(chipWidth)
      }
    })
    let totalWidth = 0
    let chipCount = 0
    for (const width of widths) {
      if (totalWidth + width <= availableWidth) {
        totalWidth += width
        chipCount++
      } else {
        break
      }
    }
    setChipWidths(widths)
    setVisibleChips(chipCount)
  }, [selectedOption])

  useEffect(() => {
    calculateVisibleChips()
    window.addEventListener('resize', calculateVisibleChips)
    return () => window.removeEventListener('resize', calculateVisibleChips)
  }, [calculateVisibleChips])

  const toggleOption = useCallback(() => {
    if (disabled) return
    setIsOpenOptions((prevOption) => !prevOption)
  }, [disabled])

  const handleOptionFalse = useCallback(() => {
    setIsOpenOptions(false)
  }, [])

  const handleSelectAll = useCallback(
    (visibleOptions: DropdownType<T>[]) => {
      const visibleValues = new Set(
        visibleOptions.map((option) => option.value)
      )
      const areAllVisibleSelected =
        visibleOptions.length > 0 &&
        visibleOptions.every((option) =>
          mapSpecificKey(selectedOption, 'value').includes(option.value)
        )

      if (areAllVisibleSelected) {
        const remainingOptions = selectedOption.filter(
          (option) =>
            !visibleValues.has(option.value) ||
            disabledDefaultValues?.includes(option.value)
        )
        onChange(remainingOptions, name)
        setSelectedOption(
          sortSelectedItems
            ? sortSelectedItems(remainingOptions)
            : remainingOptions
        )
        return
      }

      const selectionMap = new Map<string, DropdownType<T>>()
      selectedOption.forEach((option) => selectionMap.set(option.value, option))
      visibleOptions.forEach((option) => selectionMap.set(option.value, option))
      const unionArray = [...selectionMap.values()]
      onChange(unionArray, name)
      setSelectedOption(
        sortSelectedItems ? sortSelectedItems(unionArray) : unionArray
      )
    },
    [selectedOption, disabledDefaultValues, onChange, name, sortSelectedItems]
  )

  const handleValueSelect = useCallback(
    (selectedValue: DropdownType<T>) => {
      if (
        mapSpecificKey(selectedOption, 'value').includes(selectedValue?.value)
      ) {
        if (confirmationModal) {
          confirmationModal?.(Number(selectedValue.value)).then((result) => {
            if (result) {
              const fieldIndex = mapSpecificKey(
                selectedOption,
                'value'
              ).indexOf(selectedValue?.value)
              const slicedOption = [...selectedOption]
              slicedOption.splice(fieldIndex, 1)
              onChange(slicedOption, name)
              setSelectedOption(
                sortSelectedItems
                  ? sortSelectedItems([...slicedOption])
                  : [...slicedOption]
              )
            }
          })
        } else {
          const fieldIndex = mapSpecificKey(selectedOption, 'value').indexOf(
            selectedValue?.value
          )
          const slicedOption = [...selectedOption]
          slicedOption.splice(fieldIndex, 1)
          onChange(slicedOption, name)
          setSelectedOption(
            sortSelectedItems
              ? sortSelectedItems([...slicedOption])
              : [...slicedOption]
          )
          modifiedTooltip &&
            setModifiedTooltipArray((prevArr) => {
              const newArr = [...prevArr]
              newArr?.splice(fieldIndex, 1)
              return newArr
            })
        }
      } else {
        onChange([...selectedOption, selectedValue], name)
        setSelectedOption(
          sortSelectedItems
            ? sortSelectedItems([...selectedOption, selectedValue])
            : [...selectedOption, selectedValue]
        )
        const fieldIndex = options.findIndex(
          (option) => option.value === selectedValue.value
        )
        modifiedTooltip &&
          setModifiedTooltipArray([
            ...modifiedTooltipArray,
            modifiedTooltip[fieldIndex],
          ])
      }
    },
    [selectedOption, confirmationModal, onChange, name]
  )

  const clearAll = useCallback(() => {
    if (confirmationModal) {
      confirmationModal?.(null).then((result) => {
        if (result && disabledDefaultValues?.length) {
          const values = disabledDefaultValues.map(
            (val) =>
              options.find((option) => option.value === val) as DropdownType<T>
          )
          setSelectedOption(
            sortSelectedItems ? sortSelectedItems(values) : values
          )
          onChange(values, name)
        } else if (result) {
          setSelectedOption([])
          onChange([], name)
        }
      })
    } else if (disabledDefaultValues?.length) {
      const values = disabledDefaultValues.map(
        (val) =>
          options.find((option) => option.value === val) as DropdownType<T>
      )
      setSelectedOption(sortSelectedItems ? sortSelectedItems(values) : values)
      onChange(values, name)
    } else {
      setSelectedOption([])
      modifiedTooltip && setModifiedTooltipArray([])
      onChange([], name)
    }
  }, [confirmationModal, disabledDefaultValues, options, onChange, name])

  const clearChip = useCallback(
    (index: number) => {
      if (confirmationModal) {
        confirmationModal?.(Number(selectedOption[index].value)).then(
          (result) => {
            if (result) {
              const cloneSelectedValue = structuredClone(selectedOption)
              cloneSelectedValue.splice(index, 1)
              setSelectedOption(
                sortSelectedItems
                  ? sortSelectedItems(cloneSelectedValue)
                  : cloneSelectedValue
              )
              onChange(cloneSelectedValue, name)
            }
          }
        )
      } else {
        modifiedTooltip &&
          setModifiedTooltipArray((prevArr) => {
            const newArr = [...prevArr]
            newArr?.splice(index, 1)
            return newArr
          })
        const cloneSelectedValue = structuredClone(selectedOption)
        cloneSelectedValue.splice(index, 1)
        setSelectedOption(
          sortSelectedItems
            ? sortSelectedItems(cloneSelectedValue)
            : cloneSelectedValue
        )
        onChange(cloneSelectedValue, name)
      }
    },
    [confirmationModal, selectedOption, onChange, name]
  )
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case ShortcutKeys.Enter:
      case ShortcutKeys.Space:
        e.preventDefault()
        if (!isOpenOptions) {
          toggleOption()
        }
        break

      default:
        if (isOpenOptions) {
          e.preventDefault()
          toggleOption()
        }
        break
    }
  }
  useEffect(() => {
    if (!isEmpty(options)) {
      let selected = options?.filter((option: DropdownType<T>) =>
        isMultiSelectValueSelected(defaultValue, option?.value)
      )
      selected = [...selectedOption, ...selected].filter(
        (obj, index, self) =>
          index === self.findIndex((item) => item.value === obj.value)
      )

      setSelectedOption(
        sortSelectedItems ? sortSelectedItems(selected || []) : selected || []
      )
    } else {
      setSelectedOption(
        sortSelectedItems ? sortSelectedItems(selectedOption) : selectedOption
      )
    }
  }, [options])
  useEffect(() => {
    if (!isEmpty(options)) {
      const selected = options?.filter((option: DropdownType<T>) =>
        isMultiSelectValueSelected(defaultValue, option?.value)
      )
      const defautVauesNotPresentInNewOptions = selectedOption?.filter(
        (option: DropdownType<T>) =>
          isMultiSelectValueSelected(defaultValue, option?.value)
      )
      const updatedOptions = [
        ...defautVauesNotPresentInNewOptions,
        ...selected,
      ].filter(
        (obj, index, self) =>
          index === self.findIndex((item) => item.value === obj.value)
      )
      setSelectedOption(
        sortSelectedItems ? sortSelectedItems(updatedOptions) : updatedOptions
      )
      if (modifiedTooltip && modifiedTooltipArray?.length === 0) {
        const selectedIndexes = options?.reduce(
          (acc: number[], option, index) => {
            if (isMultiSelectValueSelected(defaultValue, option.value)) {
              acc.push(index)
            }
            return acc
          },
          []
        )
        setModifiedTooltipArray(
          selectedIndexes.map((i) => modifiedTooltip?.[i]).filter(Boolean)
        )
      }
    } else {
      const defautOptionNotPresentInNewOptions = selectedOption?.filter(
        (option: DropdownType<T>) =>
          isMultiSelectValueSelected(defaultValue, option?.value)
      )
      setSelectedOption(
        sortSelectedItems
          ? sortSelectedItems(defautOptionNotPresentInNewOptions)
          : defautOptionNotPresentInNewOptions
      )
    }
  }, [defaultValue])

  const updatePosition = useCallback(() => {
    if (isOpenOptions && textFieldRef.current) {
      const textFieldRect = textFieldRef.current.getBoundingClientRect()
      if (
        textFieldRect.bottom + window.scrollY <
        FIELD_VALUE_CONSTANTS.ONE_HUNDRED_TEN
      ) {
        handleOptionFalse()
      }
      const newPosition: {
        top: number
        left: number
        width: number
        maxHeight?: number
      } = {
        top: textFieldRect.bottom + window.scrollY,
        left: textFieldRect.left + window.scrollX,
        width: textFieldRect.width,
      }
      if (constrainHeight) {
        const DROPDOWN_MAX_HEIGHT = 262
        const spaceBelow = window.innerHeight - textFieldRect.bottom - 8
        newPosition.maxHeight = Math.min(spaceBelow, DROPDOWN_MAX_HEIGHT)
      }
      setPosition(newPosition)
    }
  }, [isOpenOptions, handleOptionFalse, constrainHeight])

  useEffect(() => {
    const handleScrollOrResize = (e: Event) => {
      const target = e.target as HTMLElement
      if (isOpenOptions && target?.parentElement?.id !== 'dropdown-root') {
        updatePosition()
      }
    }
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpenOptions, updatePosition])

  useEffect(() => {
    if (isOpenOptions) updatePosition()
  }, [isOpenOptions, updatePosition])

  const portalContainer =
    document.getElementById('dropdown-root') || document.createElement('div')

  if (!document.getElementById('dropdown-root')) {
    portalContainer.id = 'dropdown-root'
    document.body.appendChild(portalContainer)
  }

  const disabledDefaultValue = useCallback(
    (value: string) => {
      return disabledDefaultValues?.some((val) => val === value)
    },
    [disabledDefaultValues]
  )

  return (
    <>
      <div
        ref={chipContainerRef}
        className="absolute invisible pointer-events-none"
        style={{ position: 'absolute', left: '-9999px', ...customStyles }}
      >
        {selectedOption.map((option, index) => (
          <div
            key={`${option.value}_${index}`}
            className="bg-secondary-50 border border-secondary-25 text-accent-500 flex justify-center gap-2 items-center py-0.5 px-2 mr-1 rounded-full text-xs font-light"
          >
            <span
              style={{ ...customStyles }}
              className={`leading-5 text-xs truncate max-w-[6.25rem] ${
                (customStyles as React.CSSProperties)?.fontFamily
                  ? ''
                  : 'font-primary'
              }`}
            >
              {option.label}
            </span>
            {disabledDefaultValue(option.value) ? null : (
              <div className="p-[0.094rem]">
                <SvgFilledCross className="fill-accent-500" />
              </div>
            )}
          </div>
        ))}
      </div>
      <div
        ref={dropdownRef}
        data-qa-id={dataQaId ?? name ?? ''}
        className={` group   relative w-full font-primary  ${className}   ${
          disabled ? ' cursor-not-allowed' : 'cursor-pointer select-none'
        }`}
        onClick={isEmpty(options) ? noop : disabled ? noop : noop}
      >
        <div
          className=" max-h-10  w-full box-border relative bg-accent-0 items-center rounded "
          ref={textFieldRef}
        >
          {label ? (
            <div
              className={`translate-x-[0.875rem] translate-y-[-0.75rem] flex  text-base p-0 origin-top-left whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100%-1.75rem)] absolute left-0 top-0 z-[1] pointer-events-none ${
                disabled ? 'text-accent-300' : 'text-accent-900'
              }`}
            >
              <label
                className={` font-extraLight block text-xs leading-1 font-primary whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100% - 1.5rem]  
               ${isOpenOptions ? 'text-secondary-500' : ''}
              ${
                disabled
                  ? 'text-accent-300'
                  : 'group-hover:text-secondary-500 group-focus-within:text-secondary-500'
              }`}
                style={{ ...customStyles }}
                title={label}
              >
                {label}
              </label>
              {required ? (
                <span
                  className={`ml-1 text-xs leading-1  ${
                    disabled ? 'text-accent-300' : 'text-error-500'
                  }`}
                  style={{ ...customStyles }}
                >
                  *
                </span>
              ) : null}
            </div>
          ) : null}
          <div
            className="z-[2]  pl-4 pr-2.5 py-1.5 relative outline-none focus:border-secondary-500"
            onClick={toggleOption}
            tabIndex={tabIndex ?? 0}
            onKeyDown={handleKeyDown}
          >
            <Tooltip
              content={
                showTooltip && hasPermission
                  ? modifiedTooltipArray?.length
                    ? modifiedTooltipArray
                    : selectedOption?.length
                    ? selectedOption.map((opt) => opt.label).join(', ')
                    : ''
                  : ''
              }
            >
              <div className="flex  items-center justify-between w-full  ">
                <div
                  className={`font-medium  max-w-[calc(100%-0.938rem)] w-full overflow-y-auto overflow-x-hidden no-scroll-display items-center h-6 flex flex-wrap gap-1 hide-scrollbar
                ${disabled ? 'text-accent-500' : ''}
            ${!isEmpty(selectedOption) ? 'text-black' : 'text-accent-300'}`}
                >
                  {!isEmpty(selectedOption) && hasPermission ? (
                    <>
                      {selectedOption
                        .slice(0, visibleChips)
                        .map((option, index) => {
                          return onChipClick ? (
                            <div
                              className="text-accent-500 flex justify-center items-center gap-1  px-2 mr-1 rounded border max-w-[calc(100%-0.938rem)]"
                              onClick={(event) => {
                                event.stopPropagation()
                                if (onChipClick) {
                                  onChipClick(
                                    selectedOption?.[
                                      index
                                    ] as DocumentDropdownType<T>
                                  )
                                }
                              }}
                              data-qa-id={`${selectedOption?.[index]?.label}`}
                            >
                              <div>
                                {selectedOption?.[index]?.label?.includes(
                                  'pdf'
                                ) && <SvgPdfIcon />}
                              </div>
                              <div
                                onClick={(event) => {
                                  event.stopPropagation()
                                  if (onChipClick) {
                                    onChipClick(
                                      selectedOption?.[
                                        index
                                      ] as DocumentDropdownType<T>
                                    )
                                  }
                                }}
                                className="leading-5 truncate max-w-[13rem] text-xs font-primary text-primary-600"
                              >
                                {selectedOption?.[index]?.label}
                              </div>
                              <div
                                onClick={(event) => {
                                  event.stopPropagation()
                                  clearChip(index)
                                }}
                                data-qa-id={`${selectedOption?.[index]?.label}-cross`}
                              >
                                <SvgCrossForChip />
                              </div>
                            </div>
                          ) : (
                            <div
                              key={`${option.value}_${index}`}
                              className="bg-secondary-50 border border-secondary-25 text-accent-500 flex justify-center gap-2 items-center py-[0.125rem] px-2 mr-1 rounded-full max-w-[calc(100%-0.938rem)] text-xs font-light"
                              data-qa-id={option.label}
                            >
                              <span
                                className={`leading-5 text-xs truncate ${
                                  (customStyles as React.CSSProperties)
                                    ?.fontFamily
                                    ? ''
                                    : 'font-primary'
                                } ${chipDataWidth}`}
                                style={{ ...customStyles }}
                              >
                                {option.label}
                              </span>
                              {disabledDefaultValue(option.value) ? null : (
                                <div
                                  className="p-[0.094rem]"
                                  onClick={
                                    disabled ? noop : () => clearChip(index)
                                  }
                                  data-qa-id={`${option.label}-cross`}
                                  tabIndex={tabIndex ?? 0}
                                  onKeyDown={(e) => {
                                    if (
                                      e?.key === ShortcutKeys.Enter ||
                                      e?.key === ShortcutKeys.Space
                                    ) {
                                      if (!disabled) {
                                        clearChip(index)
                                      }
                                      e?.preventDefault()
                                      e?.stopPropagation()
                                    }
                                  }}
                                >
                                  <SvgFilledCross className="fill-accent-500" />
                                </div>
                              )}
                            </div>
                          )
                        })}
                      {selectedOption.length > visibleChips ? (
                        <div className="bg-secondary-50 text-accent-500 flex justify-center items-center py-0.5 px-2 mr-1 rounded-full">
                          <span className="leading-5 text-xs truncate max-w-[9.375rem]">
                            {`+${selectedOption.length - visibleChips}`}
                          </span>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <span
                      className={`leading-5 text-xs truncate max-w-[calc(100%-0.938rem)] w-full hide-scrollbar ${
                        disabled ? 'text-accent-500' : ''
                      }`}
                    >
                      {hasPermission ? placeholder : NAMESPACE.REDACTED}
                    </span>
                  )}
                </div>
                {selectedOption?.length > 0 && !disabled ? (
                  <div
                    className="p-1"
                    onClick={disabled ? noop : clearAll}
                    tabIndex={tabIndex ?? 0}
                    onKeyDown={(e) => {
                      if (
                        e?.key === ShortcutKeys.Enter ||
                        e?.key === ShortcutKeys.Space
                      ) {
                        if (!disabled) {
                          clearAll()
                        }
                        e?.preventDefault()
                        e?.stopPropagation()
                      }
                    }}
                  >
                    <SvgClearCross />
                  </div>
                ) : null}

                <div className="p-2">
                  <SvgDropdownArrowGrey
                    className={`transform transition-transform duration-200  ${
                      isOpenOptions ? 'rotate-180' : ''
                    } ${disabled ? 'invisible' : ''}`}
                    fill="#0A6FCD"
                  />
                </div>
              </div>
            </Tooltip>
          </div>
          <fieldset
            aria-hidden="true"
            className={`text-left  border absolute ${
              label ? 'top-[-0.938rem]' : 'top-0'
            } bottom-0 left-0 right-0 px-2 pointer-events-none rounded border-solid overflow-hidden min-w-0 
                    border-border-150
                 
                    ${
                      isOpenOptions
                        ? 'border-b-0 rounded-b-none group-hover:border-b-0 group-hover:rounded-b-none border-secondary-500'
                        : ''
                    }
                    ${
                      disabled
                        ? 'border-border-200 bg-accent-50'
                        : 'group-hover:border-secondary-500 group-focus-within:border-secondary-500'
                    }
                    ${
                      !isEmpty(errors?.[name])
                        ? 'border-error-500'
                        : 'border-border-150'
                    } `}
          >
            {label ? (
              <legend className=" invisible flex float-none max-w-full select-none  p-1 box-border z-10 ">
                <span
                  className={` font-medium  dropdown-label overflow-hidden whitespace-nowrap  text-ellipsis  
                        ${isOpenOptions ? 'text-secondary-500' : ''}
                        ${
                          disabled
                            ? 'text-accent-300'
                            : 'group-hover:text-secondary-500 group-focus-within:text-secondary-500'
                        }`}
                  style={{ ...customStyles }}
                >
                  {label}
                </span>
                {required ? (
                  <span
                    className={`ml-1 text-xs leading-1  ${
                      disabled ? 'text-accent-300' : 'text-error-500'
                    }`}
                    style={{ ...customStyles }}
                  >
                    *
                  </span>
                ) : null}
              </legend>
            ) : null}
          </fieldset>
        </div>

        {isOpenOptions
          ? ReactDOM.createPortal(
              <OptionBox
                searchLength={searchLength ?? 3}
                showFilterByText={showFilterByText}
                excludedParentRef={dropdownRef}
                handleValueSelect={handleValueSelect}
                handleOptionFalse={handleOptionFalse}
                toggleOption={toggleOption}
                dropdownOptions={options}
                isOpenOptions={isOpenOptions}
                selectedOption={selectedOption}
                isSearchable={isSearchable}
                fetchOptions={fetchOptions}
                limit={limit}
                virtualScroll={virtualScroll}
                page={currentPage}
                setPage={setCurrentPage}
                position={position}
                disabledDefaultValues={disabledDefaultValues}
                optionZIndex={optionZIndex}
                popupClassname={popupClassname}
                customCssClass={customCssClass}
                handleOptionSearch={
                  handleOptionSearch
                    ? (e) => handleOptionSearch(0, 20, e)
                    : undefined
                }
                customStyles={customStyles}
                hasSelectAllOption={hasSelectAllOption}
                onSelectAll={handleSelectAll}
              />,
              portalContainer
            )
          : null}

        {errors &&
          name &&
          (get(errors, `${name}.type`) as string) === 'required' && (
            <span
              style={{ ...customStyles }}
              className="text-error-500 text-xxs font-primary font-normal"
            >
              {NAMESPACE?.REQUIRED_FIELD}
            </span>
          )}
        {errors &&
          name &&
          (get(errors, `${name}.type`) as string) !== 'required' && (
            <span
              style={{ ...customStyles }}
              className="text-error-500 text-xxs font-primary font-normal"
            >
              {errors?.[name]?.['message']?.toString()}
            </span>
          )}
      </div>
    </>
  )
}

interface ControlledMultiSelectPropTypes<T, U extends FieldValues> {
  control: Control<U>
  name: Path<U>
  isSearchable?: boolean
  errors?: FieldErrors<U>
  onChange: (value: DropdownType<T>[], name: string) => void
  label?: string
  options: DropdownType<T>[]
  required?: boolean
  disabled?: boolean
  disabledDefaultValues?: string[]
  noOfChips?: number
  placeholder?: string
  confirmationModal?: (id: number | null) => Promise<boolean | undefined>
  dataQaId?: string
  showTooltip?: boolean
  hasPermission?: boolean
  popupClassname?: string
  customCssClass?: string
  tabIndex?: number | undefined
  fetchOptions?: (
    page: number,
    limit: number,
    value?: string
  ) => Promise<Response>
  limit?: number
  virtualScroll?: boolean
  handleOptionSearch?: (
    page: number,
    limit: number,
    vaue?: string
  ) => Promise<void>
  sortSelectedItems?: (selectedOptions: DropdownType<T>[]) => DropdownType<T>[]
  modifiedTooltip?: string[]
  chipDataWidth?: string
  onChipClick?: (value: DocumentDropdownType<T>) => void
  customStyles?: object
  showFilterByText?: boolean
  isHelperTextVisible?: string
  constrainHeight?: boolean
  hasSelectAllOption?: boolean
}

const ControlledMultiSelect = <T, U extends FieldValues>({
  control,
  name,
  errors,
  onChange,
  label = '',
  options = [],
  required,
  disabled = false,
  placeholder = '',
  noOfChips = 1,
  isSearchable = false,
  confirmationModal,
  dataQaId = '',
  showTooltip = true,
  disabledDefaultValues,
  hasPermission = true,
  popupClassname = '',
  tabIndex,
  fetchOptions,
  limit,
  virtualScroll,
  handleOptionSearch,
  sortSelectedItems,
  modifiedTooltip,
  chipDataWidth,
  customCssClass = '',
  customStyles = {},
  showFilterByText = true,
  onChipClick,
  isHelperTextVisible = '',
  constrainHeight = false,
  hasSelectAllOption = false,
}: ControlledMultiSelectPropTypes<T, U>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required }}
      disabled={disabled}
      render={({ field }) => (
        <div className="relative w-[100%]">
          <input
            className="absolute w-[2px] left-4 top-2 opacity-0 border-none"
            ref={field.ref}
            tabIndex={-1}
          />
          <MultiSelect
            showFilterByText={showFilterByText}
            disabled={field?.disabled}
            dataQaId={dataQaId}
            name={field?.name}
            label={label}
            errors={errors}
            defaultValue={field?.value}
            options={options}
            required={required}
            onChange={onChange}
            noOfChips={noOfChips}
            showTooltip={showTooltip}
            placeholder={placeholder}
            isSearchable={isSearchable}
            confirmationModal={confirmationModal}
            disabledDefaultValues={disabledDefaultValues}
            hasPermission={hasPermission}
            popupClassname={popupClassname}
            fetchOptions={fetchOptions}
            limit={limit}
            virtualScroll={virtualScroll}
            handleOptionSearch={handleOptionSearch}
            sortSelectedItems={sortSelectedItems}
            modifiedTooltip={modifiedTooltip}
            chipDataWidth={chipDataWidth}
            tabIndex={tabIndex}
            customCssClass={customCssClass}
            onChipClick={onChipClick}
            customStyles={customStyles}
            constrainHeight={constrainHeight}
            hasSelectAllOption={hasSelectAllOption}
          />
          {isHelperTextVisible && (
            <div
              className="ck-content break-words"
              dangerouslySetInnerHTML={{ __html: isHelperTextVisible }}
            ></div>
          )}
        </div>
      )}
    />
  )
}
export default ControlledMultiSelect
